import * as React from "react"

import type { ClientId, ClientRow, ClientUserOption, UserId } from "./clients-data"

export interface CreateClientInput {
  name: string
  primaryContactId: UserId | null
  notes: string | null
}

export interface ClientsStore {
  rows: ClientRow[]
  create: (input: CreateClientInput) => void
  addMember: (clientId: ClientId, userId: UserId) => void
  removeMember: (clientId: ClientId, userId: UserId) => void
  makePrimary: (clientId: ClientId, userId: UserId) => void
}

/**
 * In-memory store shaped like the mutations the API will accept. Swap this
 * hook for React-Query-backed hooks (`useClients`, `useCreateClient`, …)
 * without touching the page.
 */
export function useClientsStore(
  seed: ClientRow[],
  users: readonly ClientUserOption[],
): ClientsStore {
  const [rows, setRows] = React.useState<ClientRow[]>(seed)
  const usersById = React.useMemo(() => new Map(users.map((u) => [u.id, u])), [users])

  // When the seed arrives from a real fetch (undefined → array), or gets
  // refreshed by the caller, sync our local state. Identity comparison is
  // fine: the parent memos its seed, and a rehydrate always produces a
  // new array reference.
  const lastSeed = React.useRef(seed)
  React.useEffect(() => {
    if (lastSeed.current !== seed) {
      lastSeed.current = seed
      setRows(seed)
    }
  }, [seed])

  const create = React.useCallback(
    ({ name, primaryContactId, notes }: CreateClientInput) => {
      setRows((prev) => {
        const contact = primaryContactId ? usersById.get(primaryContactId) : null
        const next: ClientRow = {
          id: `c-${Date.now()}` as ClientId,
          name,
          primaryContactId: contact ? contact.id : null,
          notes,
          isActive: true,
          createdAt: new Date().toISOString(),
          members: contact
            ? [
                {
                  userId: contact.id,
                  name: contact.name,
                  email: contact.email,
                  isPrimary: true,
                  lastSeen: null,
                },
              ]
            : [],
          reviewCount: 0,
          openReviewCount: 0,
        }
        return [next, ...prev]
      })
    },
    [usersById],
  )

  const addMember = React.useCallback(
    (clientId: ClientId, userId: UserId) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== clientId) return r
          if (r.members.some((mem) => mem.userId === userId)) return r
          const user = usersById.get(userId)
          if (!user) return r
          const makePrimary = r.primaryContactId === null && r.members.length === 0
          return {
            ...r,
            primaryContactId: makePrimary ? user.id : r.primaryContactId,
            members: [
              ...r.members,
              {
                userId: user.id,
                name: user.name,
                email: user.email,
                isPrimary: makePrimary,
                lastSeen: null,
              },
            ],
          }
        }),
      )
    },
    [usersById],
  )

  const removeMember = React.useCallback((clientId: ClientId, userId: UserId) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== clientId) return r
        return {
          ...r,
          primaryContactId: r.primaryContactId === userId ? null : r.primaryContactId,
          members: r.members.filter((mem) => mem.userId !== userId),
        }
      }),
    )
  }, [])

  const makePrimary = React.useCallback((clientId: ClientId, userId: UserId) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id !== clientId
          ? r
          : {
              ...r,
              primaryContactId: userId,
              members: r.members.map((mem) => ({
                ...mem,
                isPrimary: mem.userId === userId,
              })),
            },
      ),
    )
  }, [])

  return { rows, create, addMember, removeMember, makePrimary }
}
